import { useEffect, useState, useContext, useMemo, useRef } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import CardLoading from 'shared/card-loading'
import CardError from 'shared/card-error'
import Button from 'react-bootstrap/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faLayerGroup,
  faEye,
  faEdit,
  faCopy,
  faExclamationTriangle,
  faPlus
} from '@fortawesome/free-solid-svg-icons'
import Page from 'layout/page'
import PageTitle from 'layout/page-title'
import { FOLDER_PARAM, folderReturnCrumbUrl } from 'dashboard/folders/folder-context'
import UserContext from 'context/user-context'
import TopTransactions from './top-transactions'
import Tabs from '../duties/shared/tabs'
import DashboardSelectModal from './dashboard-select-modal'
import DashboardGrid from './dashboard-grid'
import ShareButton from 'shared/buttons/share-button'
import { useCreateDashboardUser } from 'api/hooks/use-dashboard-user'
import DashboardUserRow from './user-row'
import CreateTemplateModal from './create-template-modal'
import DashboardExports from './exports'
import SamplesTab from './samples'
import useSections from 'api/hooks/use-sections'
import {
  useDashboard,
  useDashboards,
  useUpdateDashboard,
  useDuplicateDashboard
} from 'api/hooks/use-dashboards'
import { useCreateDashboardTemplate } from 'api/hooks/use-dashboard-templates'
import { useCreateDashboardAccountSearches } from 'api/hooks/use-dashboard-account-searches'
import { useBulkDestroyDashboardVendors } from 'api/hooks/use-dashboard-vendors'
import { useBulkDestroyDashboardEmployees } from 'api/hooks/use-dashboard-employees'
import DashboardFilters from './filters'
import AiSearchBar from './ai-search-bar'
import { ReceiptDropzone, ReceiptManagement } from 'receipt-scans'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { buildAccountParams, baseUrl } from 'api/helpers'
import NewView from './new'
import { useTableColumns } from 'api/hooks/use-table-columns'
import { getMinMaxKeys, minMaxParamFilters } from './utils'
import { isUndefined, isNull, isEmpty, startCase } from 'lodash'
import { useQuery, useQueryClient } from 'react-query'
import customColumnService from 'services/customColumnService'
import AuthContext from 'context/auth-context'
import { toast } from 'react-toastify'
import ExportPdfButton from './export-pdf-button'
import { viewPath } from 'helpers/utils'
import { parseFilterTree, savedFilterTree } from 'api/filter-tree'

const cleanDashboardParams = (params, tableColumns, customColumns = []) => {
  if (!params || typeof params !== 'object') return params

  const cleaned = { ...params }

  // Step 1: Remove undefined, null, and empty values (empty strings and empty arrays)
  Object.keys(cleaned).forEach((key) => {
    if (
      isUndefined(cleaned[key]) ||
      isNull(cleaned[key]) ||
      ((typeof cleaned[key] === 'string' || Array.isArray(cleaned[key])) &&
        isEmpty(cleaned[key]))
    ) {
      delete cleaned[key]
    }
  })

  if (!tableColumns) return cleaned

  // Step 2: Remove invalid operator params - those without corresponding accessor keys
  Object.keys(cleaned).forEach((key) => {
    if (key.endsWith('Operator')) {
      const accessorKey = key.replace('Operator', '')
      const operatorValue = cleaned[key]

      // Keep operators if they are 'is_empty' or 'is_not_empty'
      if (operatorValue === 'is_empty' || operatorValue === 'is_not_empty') {
        return
      }

      // Remove operator if it's 'contains' and the corresponding accessor key doesn't exist
      if (
        operatorValue === 'contains' &&
        !cleaned.hasOwnProperty(accessorKey)
      ) {
        delete cleaned[key]
      }
    }
  })

  // Step 2b: same for a mode left behind by step 1 (mirrors DashboardParamsCleaner).
  Object.keys(cleaned).forEach((key) => {
    if (key.endsWith('Mode') && !cleaned.hasOwnProperty(key.replace(/Mode$/, ''))) {
      delete cleaned[key]
    }
  })

  // Step 3: Clean min/max params for date, min_max, and min_max_percent filter types
  tableColumns.forEach((column) => {
    if (!minMaxParamFilters.includes(column.filter_type)) return

    const { minKey, maxKey } = getMinMaxKeys(column)
    let optionKey = null

    if (column.filter_type === 'date') {
      optionKey = `${column.accessor_key}Option`
    }

    const minEmpty = !cleaned.hasOwnProperty(minKey) || cleaned[minKey] === ''
    const maxEmpty = !cleaned.hasOwnProperty(maxKey) || cleaned[maxKey] === ''
    const optionEmpty =
      !optionKey ||
      !cleaned.hasOwnProperty(optionKey) ||
      cleaned[optionKey] === ''

    // Remove min/max params if both are empty and no option is set (for date)
    // Or if both are empty (for min_max and min_max_percent)
    const shouldClean =
      column.filter_type === 'date'
        ? minEmpty && maxEmpty && optionEmpty
        : minEmpty && maxEmpty

    if (shouldClean) {
      delete cleaned[minKey]
      delete cleaned[maxKey]
      if (optionKey) {
        delete cleaned[optionKey]
      }
    }
  })

  // Step 4: Clean min/max params for custom columns
  customColumns.forEach((cc) => {
    if (cc.column_type === 'number' || cc.column_type === 'date') {
      const minKey = `custom_column_min_${cc.id}`
      const maxKey = `custom_column_max_${cc.id}`
      const optionKey = `custom_column_${cc.id}Option`

      const minEmpty = !cleaned.hasOwnProperty(minKey) || cleaned[minKey] === ''
      const maxEmpty = !cleaned.hasOwnProperty(maxKey) || cleaned[maxKey] === ''
      const optionEmpty =
        !cleaned.hasOwnProperty(optionKey) || cleaned[optionKey] === ''

      // Remove min/max params if both are empty
      // For date columns, also check if option is empty
      const shouldClean =
        cc.column_type === 'date'
          ? minEmpty && maxEmpty && optionEmpty
          : minEmpty && maxEmpty

      if (shouldClean) {
        delete cleaned[minKey]
        delete cleaned[maxKey]
        if (cc.column_type === 'date') {
          delete cleaned[optionKey]
        }
      }
    }
  })

  return cleaned
}

function Show({
  title,
  dashboardKey,
  defaultParams = {},
  showYear = true,
  showAnalytics = true
}) {
  const [searchParams] = useSearchParams()
  const id = searchParams.get('dashboard_id')
  const viewParam = searchParams.get('view')
  const folderId = searchParams.get(FOLDER_PARAM)
  const navigate = useNavigate()
  const user = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  const userDashboards = user[dashboardKey] || []
  const dashboardUrlId = id || (userDashboards[0] && userDashboards[0].id)

  if (!dashboardUrlId) {
    // eslint-disable-next-line no-unused-vars
    const [_initialString, rootPath, sectionName] =
      window.location.pathname.split('/')

    localStorage.setItem(
      'toast',
      `You must first create a view for ${startCase(sectionName)}`
    )
    navigate(`/${rootPath}`, { replace: true })
  }

  useEffect(() => {
    if (!id && userDashboards.length) {
      const params = new URLSearchParams(window.location.search)
      params.set('dashboard_id', dashboardUrlId)
      const newUrl = `${window.location.pathname}?${params.toString()}`
      navigate(newUrl, { replace: true })
    }
  }, [id, dashboardUrlId, navigate, userDashboards.length])

  const [dashboardId, setDashboardId] = useState(dashboardUrlId)

  // Sync dashboardId state with URL parameter changes
  useEffect(() => {
    if (dashboardUrlId && dashboardUrlId !== dashboardId) {
      setDashboardId(dashboardUrlId)
    }
  }, [dashboardUrlId, dashboardId])

  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [showStats, setShowStats] = useState(true)
  const [activeTab, setActiveTab] = useState(() => {
    if (viewParam === 'dashboard') return 'dashboard'
    if (viewParam === 'exports') return 'exports'
    if (viewParam === 'receipts') return 'receipts'
    if (viewParam === 'invoice-scans') return 'invoice-scans'
    if (viewParam === 'samples') return 'samples'
    return 'data'
  })
  const [showDashboardModal, setShowDashboardModal] = useState(false)
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false)
  const [isPreviewMode, setIsPreviewMode] = useState(false)
  const [referenceSearch, setReferenceSearch] = useState(null)
  const {
    isSuccess: isSectionSuccess,
    isLoading: isSectionLoading,
    data: sections
  } = useSections({
    options: {}
  })

  const defaultSection = isSectionSuccess && sections[0]
  const options = { enabled: !!dashboardId && !isFilterOpen }
  const sectionId = userDashboards[0]?.section_id

  const { isSuccess: isColumnsSuccess, data: tableColumns } = useTableColumns({
    params: { section_id: sectionId },
    options: {}
  })
  const { data: customColumns = [] } = useQuery(
    ['customColumns', dashboardId],
    () => customColumnService.getCustomColumns(token, dashboardId),
    {
      enabled: !!dashboardId && !!token
    }
  )
  const { isSuccess: isDashboardsSuccess, data: dashboards } = useDashboards({
    params: { section_id: sectionId }
  })
  const {
    data: dashboard,
    isSuccess: isDashboardSuccess,
    isLoading: isDashboardLoading,
    isError: isDashboardError,
    isFetching: isDashboardFetching,
    refetch: refetchDashboard
  } = useDashboard({
    id: dashboardId,
    options
  })
  const isUserCreator = dashboard?.user_id === user.id
  const isDashboardEditor = dashboard?.shared_users?.find(
    (du) => du.user_id === user.id
  )?.is_editor
  const isFolderEditor = dashboard?.is_folder_editor
  const canEdit = isUserCreator || isDashboardEditor || isFolderEditor

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    const params = new URLSearchParams(window.location.search)
    if (tab === 'dashboard') {
      params.set('view', 'dashboard')
    } else if (tab === 'exports') {
      params.set('view', 'exports')
    } else if (tab === 'receipts') {
      params.set('view', 'receipts')
    } else if (tab === 'invoice-scans') {
      params.set('view', 'invoice-scans')
    } else if (tab === 'samples') {
      params.set('view', 'samples')
    } else {
      params.delete('view')
    }
    const search = params.toString()
    navigate(`${window.location.pathname}${search ? `?${search}` : ''}`, {
      replace: true
    })
  }

  const baseParams = {
    name: defaultSection?.name || '',
    risk_threshold: 0,
    section_id: defaultSection?.id || '',
    minimum_amount: '',
    maximum_amount: '',
    dashboardId,
    vendors: [],
    excludedVendors: [],
    employees: [],
    analytics: [],
    account_search: '',
    orderBy: 'transaction_on',
    orderDirection: 'desc',
    orgsOperator: 'include',
    objectsOperator: 'include',
    projectsOperator: 'include',
    segOnesOperator: 'include',
    segTwosOperator: 'include',
    segThreesOperator: 'include',
    segFoursOperator: 'include',
    segFivesOperator: 'include',
    segSixesOperator: 'include',
    segSevensOperator: 'include',
    segEightsOperator: 'include',
    orgs: [],
    objects: [],
    projects: [],
    segOnes: [],
    segTwos: [],
    segThrees: [],
    segFours: [],
    segFives: [],
    segSixes: [],
    segSevens: [],
    segEights: [],
    filterTree: null,
    ...defaultParams
  }

  if (isColumnsSuccess) {
    tableColumns.forEach((tc) => {
      if (baseParams[tc.accessor_key] === undefined) {
        baseParams[tc.accessor_key] = ''
      }
      // Don't add default operators - they should only exist when there's a value
      // if (
      //   tc.filter_type === 'basic' &&
      //   baseParams[`${tc.accessor_key}Operator`] === undefined
      // ) {
      //   baseParams[`${tc.accessor_key}Operator`] = 'contains'
      // }
      if (minMaxParamFilters.includes(tc.filter_type)) {
        const { minKey, maxKey } = getMinMaxKeys(tc)
        if (baseParams[minKey] === undefined) {
          baseParams[minKey] = ''
        }
        if (baseParams[maxKey] === undefined) {
          baseParams[maxKey] = ''
        }
        if (tc.filter_type === 'date') {
          const optionKey = `${tc.accessor_key}Option`
          baseParams[optionKey] = baseParams[optionKey] || ''
        }
      }
    })
  }

  const [params, setParams] = useState({ ...baseParams })

  useEffect(() => {
    if (isColumnsSuccess) {
      setParams((p) => {
        const updated = { ...p }
        tableColumns.forEach((tc) => {
          if (updated[tc.accessor_key] === undefined) {
            updated[tc.accessor_key] = ''
          }
          // Don't add default operators - they should only exist when there's a value
          // if (
          //   tc.filter_type === 'basic' &&
          //   updated[`${tc.accessor_key}Operator`] === undefined
          // ) {
          //   updated[`${tc.accessor_key}Operator`] = 'contains'
          // }
          if (minMaxParamFilters.includes(tc.filter_type)) {
            const { minKey, maxKey } = getMinMaxKeys(tc)
            if (updated[minKey] === undefined) {
              updated[minKey] = ''
            }
            if (updated[maxKey] === undefined) {
              updated[maxKey] = ''
            }
            if (tc.filter_type === 'date') {
              const optionKey = `${tc.accessor_key}Option`
              if (updated[optionKey] === undefined) {
                updated[optionKey] = ''
              }
            }
          }
        })
        return updated
      })
    }
  }, [isColumnsSuccess, tableColumns])

  const sectionName = isSectionSuccess
    ? sections.find((s) => s.id === params.section_id)?.name
    : ''
  const sectionUnit = isSectionSuccess
    ? sections.find((s) => s.id === params.section_id)?.unit
    : ''

  useEffect(() => {
    if (isDashboardSuccess && dashboardId) {
      setParams(() => {
        const updated = {
          ...baseParams,
          name: dashboard?.name,
          section_id: dashboard?.section_id,
          dashboardId,
          minimum_amount: dashboard?.minimum_amount || '',
          maximum_amount: dashboard?.maximum_amount || '',
          vendors: dashboard?.vendors || [],
          excludedVendors: dashboard?.excluded_vendors || [],
          employees: dashboard?.employees || [],
          analytics: dashboard?.analytics || [],
          orgsOperator: dashboard?.account_search?.orgs_operator || 'include',
          objectsOperator:
            dashboard?.account_search?.objects_operator || 'include',
          projectsOperator:
            dashboard?.account_search?.projects_operator || 'include',
          segOnesOperator:
            dashboard?.account_search?.seg_ones_operator || 'include',
          segTwosOperator:
            dashboard?.account_search?.seg_twos_operator || 'include',
          segThreesOperator:
            dashboard?.account_search?.seg_threes_operator || 'include',
          segFoursOperator:
            dashboard?.account_search?.seg_fours_operator || 'include',
          segFivesOperator:
            dashboard?.account_search?.seg_fives_operator || 'include',
          segSixesOperator:
            dashboard?.account_search?.seg_sixes_operator || 'include',
          segSevensOperator:
            dashboard?.account_search?.seg_sevens_operator || 'include',
          segEightsOperator:
            dashboard?.account_search?.seg_eights_operator || 'include',
          orgs: dashboard?.account_search?.orgs || [],
          objects: dashboard?.account_search?.objects || [],
          projects: dashboard?.account_search?.projects || [],
          segOnes: dashboard?.account_search?.seg_ones || [],
          segTwos: dashboard?.account_search?.seg_twos || [],
          segThrees: dashboard?.account_search?.seg_threes || [],
          segFours: dashboard?.account_search?.seg_fours || [],
          segFives: dashboard?.account_search?.seg_fives || [],
          segSixes: dashboard?.account_search?.seg_sixes || [],
          segSevens: dashboard?.account_search?.seg_sevens || [],
          segEights: dashboard?.account_search?.seg_eights || [],
          orgs_search: dashboard?.account_search?.orgs_search || '',
          objects_search: dashboard?.account_search?.objects_search || '',
          projects_search: dashboard?.account_search?.projects_search || '',
          seg_ones_search: dashboard?.account_search?.seg_ones_search || '',
          seg_twos_search: dashboard?.account_search?.seg_twos_search || '',
          seg_threes_search: dashboard?.account_search?.seg_threes_search || '',
          seg_fours_search: dashboard?.account_search?.seg_fours_search || '',
          seg_fives_search: dashboard?.account_search?.seg_fives_search || '',
          seg_sixes_search: dashboard?.account_search?.seg_sixes_search || '',
          seg_sevens_search: dashboard?.account_search?.seg_sevens_search || '',
          seg_eights_search: dashboard?.account_search?.seg_eights_search || '',
          filterTree: parseFilterTree(dashboard?.params?.filterTree)
        }

        // Include orderBy and orderDirection from dashboard params
        // Note: transactionListId is intentionally excluded — it is session-only
        // so that shared users see the table in an unfiltered-by-list state
        if (dashboard?.params?.orderBy !== undefined) {
          updated.orderBy = dashboard.params.orderBy
        }
        if (dashboard?.params?.orderDirection !== undefined) {
          updated.orderDirection = dashboard.params.orderDirection
        }

        Object.keys(defaultParams).forEach((k) => {
          if (dashboard?.params?.[k] !== undefined) {
            updated[k] = dashboard.params[k]
          }
        })

        if (isColumnsSuccess) {
          tableColumns.forEach((tc) => {
            // Always update from dashboard params if they exist, don't check for undefined
            // This ensures saved filter values are loaded on page refresh
            if (dashboard?.params?.[tc.accessor_key] !== undefined) {
              updated[tc.accessor_key] = dashboard.params[tc.accessor_key]
            } else if (updated[tc.accessor_key] === undefined) {
              updated[tc.accessor_key] = ''
            }

            if (tc.filter_type === 'basic') {
              if (
                dashboard?.params?.[`${tc.accessor_key}Operator`] !== undefined
              ) {
                updated[`${tc.accessor_key}Operator`] =
                  dashboard.params[`${tc.accessor_key}Operator`]
              }
              // Don't add default operators - they should only exist when there's a value
              // else if (updated[`${tc.accessor_key}Operator`] === undefined) {
              //   updated[`${tc.accessor_key}Operator`] = 'contains'
              // }
            }
            if (
              tc.filter_type === 'multiselect' ||
              tc.filter_type === 'mcc_select' ||
              tc.filter_type === 'group_multiselect'
            ) {
              const modeKey = `${tc.accessor_key}Mode`
              if (dashboard?.params?.[modeKey] !== undefined) {
                updated[modeKey] = dashboard.params[modeKey]
              }
            }
            if (minMaxParamFilters.includes(tc.filter_type)) {
              const { minKey, maxKey } = getMinMaxKeys(tc)
              updated[minKey] = dashboard?.params?.[minKey] || ''
              updated[maxKey] = dashboard?.params?.[maxKey] || ''
              if (tc.filter_type === 'date') {
                const optionKey = `${tc.accessor_key}Option`
                updated[optionKey] = dashboard?.params?.[optionKey] || ''
              }
            }
          })
        }

        // Load custom column filter parameters
        if (customColumns.length > 0) {
          customColumns.forEach((cc) => {
            const baseKey = `custom_column_${cc.id}`

            // Load main filter value
            if (dashboard?.params?.[baseKey] !== undefined) {
              updated[baseKey] = dashboard.params[baseKey]
            } else if (updated[baseKey] === undefined) {
              updated[baseKey] = ''
            }

            // Load operator for text columns
            if (cc.column_type === 'text') {
              const operatorKey = `${baseKey}Operator`
              if (dashboard?.params?.[operatorKey] !== undefined) {
                updated[operatorKey] = dashboard.params[operatorKey]
              }
            }

            // Load min/max for number and date columns
            if (cc.column_type === 'number' || cc.column_type === 'date') {
              const minKey = `custom_column_min_${cc.id}`
              const maxKey = `custom_column_max_${cc.id}`

              if (dashboard?.params?.[minKey] !== undefined) {
                updated[minKey] = dashboard.params[minKey]
              } else if (updated[minKey] === undefined) {
                updated[minKey] = ''
              }

              if (dashboard?.params?.[maxKey] !== undefined) {
                updated[maxKey] = dashboard.params[maxKey]
              } else if (updated[maxKey] === undefined) {
                updated[maxKey] = ''
              }

              // Load date option for date columns
              if (cc.column_type === 'date') {
                const optionKey = `${baseKey}Option`
                if (dashboard?.params?.[optionKey] !== undefined) {
                  updated[optionKey] = dashboard.params[optionKey]
                } else if (updated[optionKey] === undefined) {
                  updated[optionKey] = ''
                }
              }
            }
          })
        }

        return updated
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDashboardSuccess,
    dashboardId,
    setParams,
    isColumnsSuccess,
    tableColumns,
    customColumns
  ])

  const { mutate } = useUpdateDashboard({
    id: dashboardId,
    options: {
      onSuccess: () => {
        // Invalidate the dashboard query to ensure fresh data on browser navigation
        queryClient.invalidateQueries([`dashboards/${dashboardId}`])
      }
    }
  })

  const { mutate: bulkDestroyVendors } = useBulkDestroyDashboardVendors({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries(`dashboards/${dashboardId}`)
        queryClient.invalidateQueries('transactions')
      }
    }
  })

  const { mutate: bulkDestroyEmployees } = useBulkDestroyDashboardEmployees({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries(`dashboards/${dashboardId}`)
        queryClient.invalidateQueries('employees')
        queryClient.invalidateQueries('transactions')
      }
    }
  })

  const { mutate: createAccountSearch } = useCreateDashboardAccountSearches({
    options: {
      onError: (error) => {
        console.error('Failed to save account search:', error)
      }
    }
  })

  const { mutate: duplicateDashboard } = useDuplicateDashboard({
    id: dashboardId,
    options: {
      onSuccess: (data) => {
        setDashboardId(data.id)
        navigate(viewPath(sectionName, data.id) || '/dashboards')
      }
    }
  })

  const { mutate: createTemplate, isLoading: isCreatingTemplate } =
    useCreateDashboardTemplate({
      options: {
        onSuccess: (data) => {
          setShowCreateTemplateModal(false)
          toast.success(`Template "${data.name}" created successfully!`)
        },
        onError: (error) => {
          toast.error(`Failed to create template: ${error.message}`)
        }
      }
    })

  const handleCreateTemplate = (templateData) => {
    createTemplate({
      sectionId,
      dashboardId,
      templateData
    })
  }

  const paramsKey = useMemo(() => JSON.stringify(params), [params])
  const initialParamsRef = useRef(null)

  useEffect(() => {
    if (!isDashboardSuccess || !dashboardId) return
    if (initialParamsRef.current === null) {
      initialParamsRef.current = paramsKey
      return
    }
    if (initialParamsRef.current !== paramsKey) {
      if (canEdit) {
        const dashboardSettings = {
          params: cleanDashboardParams(
            {
              orderBy: params.orderBy,
              orderDirection: params.orderDirection,
              filterTree: savedFilterTree(params.filterTree),
              ...Object.keys(defaultParams).reduce((acc, key) => {
                acc[key] = params[key]
                return acc
              }, {}),
              ...(isColumnsSuccess
                ? Object.keys(params).reduce((acc, paramKey) => {
                    const tableColumn = tableColumns.find(
                      (tc) => tc.accessor_key === paramKey
                    )
                    if (tableColumn) {
                      acc[paramKey] = params[paramKey]
                      if (
                        tableColumn.filter_type === 'basic' &&
                        params[`${paramKey}Operator`]
                      ) {
                        acc[`${paramKey}Operator`] =
                          params[`${paramKey}Operator`]
                      }
                      if (
                        (tableColumn.filter_type === 'multiselect' ||
                          tableColumn.filter_type === 'mcc_select' ||
                          tableColumn.filter_type === 'group_multiselect') &&
                        params[`${paramKey}Mode`]
                      ) {
                        acc[`${paramKey}Mode`] = params[`${paramKey}Mode`]
                      }
                      if (
                        minMaxParamFilters.includes(tableColumn.filter_type)
                      ) {
                        const { minKey, maxKey } = getMinMaxKeys(tableColumn)
                        if (params[minKey] !== undefined) {
                          acc[minKey] = params[minKey] || ''
                        }
                        if (params[maxKey] !== undefined) {
                          acc[maxKey] = params[maxKey] || ''
                        }
                        if (tableColumn.filter_type === 'date') {
                          const optionKey = `${tableColumn.accessor_key}Option`
                          if (
                            !isUndefined(params[optionKey]) &&
                            !isEmpty(params[optionKey])
                          ) {
                            acc[optionKey] = params[optionKey] || ''
                          }
                        }
                      }
                    }
                    return acc
                  }, {})
                : {}),
              // Include custom column parameters
              ...(customColumns.length > 0
                ? Object.keys(params).reduce((acc, paramKey) => {
                    // Check if this is a custom column parameter
                    const isCustomColumnParam = customColumns.some((cc) => {
                      const baseKey = `custom_column_${cc.id}`
                      const operatorKey = `${baseKey}Operator`
                      const minKey = `custom_column_min_${cc.id}`
                      const maxKey = `custom_column_max_${cc.id}`
                      const optionKey = `${baseKey}Option`
                      return (
                        paramKey === baseKey ||
                        paramKey === operatorKey ||
                        paramKey === minKey ||
                        paramKey === maxKey ||
                        paramKey === optionKey
                      )
                    })

                    if (
                      isCustomColumnParam &&
                      params[paramKey] !== undefined &&
                      params[paramKey] !== ''
                    ) {
                      acc[paramKey] = params[paramKey]
                    }
                    return acc
                  }, {})
                : {})
            },
            tableColumns,
            customColumns
          ),
          account_search: {
            ...Object.keys(buildAccountParams(params)).reduce((acc, key) => {
              // Extract array values from joined strings and keep operators as-is
              if (key.endsWith('_ids')) {
                const baseKey = key.replace('_ids', 's')
                acc[baseKey] = params[baseKey] || []
              } else if (key.endsWith('_operator')) {
                acc[key] = params[key.replace('_', '')] || 'include'
              }
              return acc
            }, {}),
            orgs_search: params.orgs_search || '',
            objects_search: params.objects_search || '',
            projects_search: params.projects_search || '',
            seg_ones_search: params.seg_ones_search || '',
            seg_twos_search: params.seg_twos_search || '',
            seg_threes_search: params.seg_threes_search || '',
            seg_fours_search: params.seg_fours_search || '',
            seg_fives_search: params.seg_fives_search || '',
            seg_sixes_search: params.seg_sixes_search || '',
            seg_sevens_search: params.seg_sevens_search || '',
            seg_eights_search: params.seg_eights_search || ''
          }
        }
        mutate(dashboardSettings)

        // Also save account search data separately
        const accountSearchData = {
          dashboard_id: dashboardId,
          search: '',
          org: params.orgs || [],
          object: params.objects || [],
          project: params.projects || [],
          seg_one: params.segOnes || [],
          seg_two: params.segTwos || [],
          seg_three: params.segThrees || [],
          seg_four: params.segFours || [],
          seg_five: params.segFives || [],
          seg_six: params.segSixes || [],
          seg_seven: params.segSevens || [],
          seg_eight: params.segEights || [],
          orgs_operator: params.orgsOperator || 'include',
          objects_operator: params.objectsOperator || 'include',
          projects_operator: params.projectsOperator || 'include',
          seg_ones_operator: params.segOnesOperator || 'include',
          seg_twos_operator: params.segTwosOperator || 'include',
          seg_threes_operator: params.segThreesOperator || 'include',
          seg_fours_operator: params.segFoursOperator || 'include',
          seg_fives_operator: params.segFivesOperator || 'include',
          seg_sixes_operator: params.segSixesOperator || 'include',
          seg_sevens_operator: params.segSevensOperator || 'include',
          seg_eights_operator: params.segEightsOperator || 'include'
        }
        createAccountSearch(accountSearchData)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey])

  // A view opened from a folder on the index carries that folder on its URL, so the crumb returns
  // to the folder it was opened from — however deeply that folder is nested.
  const crumbs = [{ name: 'Views', url: folderReturnCrumbUrl(folderId) }]

  return (
    <Page
      title={
        <Row>
          <Col sm="auto">Views</Col>
          <Col sm="auto"></Col>
        </Row>
      }
      pageTitle={`View - ${isDashboardSuccess ? dashboard?.name : ''}`}
    >
      <PageTitle breadcrumbs={crumbs}>{title}</PageTitle>

      {isDashboardSuccess && (
        <>
          <Card className="mb-3">
            <Card.Body>
              <Row className="my-1 align-items-center">
                <Col className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <h3 className="mb-0 me-2">{params.name}</h3>
                    <Button
                      variant="light"
                      size="sm"
                      className="me-2"
                      onClick={() => setShowDashboardModal(true)}
                    >
                      <FontAwesomeIcon icon={faLayerGroup} /> Views
                    </Button>
                    <Button
                      variant="light"
                      size="sm"
                      className="me-2"
                      onClick={() => duplicateDashboard()}
                    >
                      <FontAwesomeIcon icon={faCopy} /> Duplicate
                    </Button>
                    {user.is_admin && (
                      <Button
                        variant="light"
                        size="sm"
                        className="me-2"
                        onClick={() => setShowCreateTemplateModal(true)}
                      >
                        <FontAwesomeIcon icon={faPlus} /> Create Template
                      </Button>
                    )}
                    <div className="fw-bold me-2">
                      <NewView
                        dashboard={dashboard}
                        setParams={setParams}
                        isUserCreator={isUserCreator}
                      />
                    </div>
                    <ShareButton
                      resource={dashboard}
                      resourceName={dashboard?.name}
                      useCreateMutation={useCreateDashboardUser}
                      createParams={{ dashboard_id: dashboard?.id }}
                      queryKeys={[`dashboards/${dashboard?.id}`, 'dashboards']}
                      UserRowComponent={DashboardUserRow}
                      userRowProps={(u) => ({ dashboardUser: u, editable: isUserCreator, dashboardId: dashboard?.id })}
                      buttonText="Share"
                    />
                  </div>
                  <div>
                    <NewView
                      sectionId={dashboard?.section_id || sectionId}
                      sectionName={sectionName}
                      setDashboardId={setDashboardId}
                      setParams={setParams}
                      baseParams={params}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col>
                  {dashboard.description && (
                    <div className="mb-2">
                      <small className="text-muted">
                        {dashboard.description}
                      </small>
                    </div>
                  )}
                  {dashboard.owner && (
                    <div className="text-muted">
                      Owner: {dashboard.owner.first_name}{' '}
                      {dashboard.owner.last_name}
                    </div>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </>
      )}

      {isDashboardsSuccess && (
        <DashboardSelectModal
          show={showDashboardModal}
          handleClose={() => setShowDashboardModal(false)}
          dashboards={dashboards.data}
          currentId={dashboardId}
          sectionName={sectionName}
          setDashboardId={setDashboardId}
        />
      )}

      <CreateTemplateModal
        show={showCreateTemplateModal}
        onHide={() => setShowCreateTemplateModal(false)}
        dashboard={dashboard}
        onSubmit={handleCreateTemplate}
        isLoading={isCreatingTemplate}
      />

      <Row className="my-3">
        <Col xs={12} sm={6} className="d-flex align-items-center">
          <Tabs
            tabs={[
              { name: 'Data', value: 'data' },
              { name: 'Dashboard', value: 'dashboard' },
              { name: 'Exports', value: 'exports' },
              { name: 'Samples', value: 'samples' },
              ...(dashboardKey === 'pc_dashboards' ? [{ name: 'Receipts', value: 'receipts' }] : []),
              ...(dashboardKey === 'ap_dashboards' ? [{ name: 'Invoice Scans', value: 'invoice-scans' }] : [])
            ]}
            activeTab={activeTab}
            setActiveTab={handleTabChange}
          />
        </Col>
      </Row>

      {isDashboardSuccess && !canEdit && (
        <Card
          className="bg-warning border-warning mb-3"
          style={{ '--bs-bg-opacity': 0.2 }}
        >
          <Card.Body className="py-2">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-warning me-2"
              />
              <div className="text-dark">
                <strong>Viewer Mode:</strong> You cannot edit filters or modify
                this view.
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {activeTab === 'data' && isDashboardSuccess && (
        <>
          {canEdit && (
            <Row>
              <Col>
                <DashboardFilters
                  params={params}
                  setParams={setParams}
                  isFilterOpen={isFilterOpen}
                  setIsFilterOpen={setIsFilterOpen}
                  sectionName={sectionName}
                  showStats={showStats}
                  setShowStats={setShowStats}
                  sectionId={sectionId}
                  showYear={showYear}
                  showAnalytics={false}
                />
              </Col>
            </Row>
          )}
          {user.municipality?.setting?.ai_search_enabled && (
            <Row className="mt-2">
              <Col>
                <AiSearchBar
                  dashboardId={dashboardId}
                  params={params}
                  setParams={setParams}
                  onReferenceSearch={setReferenceSearch}
                  onClearAll={() => {
                    setParams((prev) => {
                      const cleared = { ...prev }

                      // Clear standard column filters to empty/default values
                      tableColumns.forEach((tc) => {
                        cleared[tc.accessor_key] = ''
                        cleared[`${tc.accessor_key}Operator`] = ''
                        if (minMaxParamFilters.includes(tc.filter_type)) {
                          const { minKey, maxKey } = getMinMaxKeys(tc)
                          cleared[minKey] = ''
                          cleared[maxKey] = ''
                        }
                        if (tc.filter_type === 'date') {
                          cleared[`${tc.accessor_key}Option`] = ''
                        }
                      })

                      // Clear custom column filters
                      Object.keys(prev).forEach(key => {
                        if (key.startsWith('custom_column_')) {
                          cleared[key] = ''
                        }
                      })

                      // Clear other filter-specific params
                      cleared.minimum_amount = ''
                      cleared.maximum_amount = ''
                      cleared.analytics = []
                      cleared.vendors = []
                      cleared.excludedVendors = []
                      cleared.employees = []
                      cleared.orgs = []
                      cleared.objects = []
                      cleared.projects = []
                      cleared.segOnes = []
                      cleared.segTwos = []
                      cleared.segThrees = []
                      cleared.segFours = []
                      cleared.segFives = []
                      cleared.segSixes = []
                      cleared.segSevens = []
                      cleared.segEights = []
                      cleared.orgsOperator = 'include'
                      cleared.objectsOperator = 'include'
                      cleared.projectsOperator = 'include'
                      cleared.segOnesOperator = 'include'
                      cleared.segTwosOperator = 'include'
                      cleared.segThreesOperator = 'include'
                      cleared.segFoursOperator = 'include'
                      cleared.segFivesOperator = 'include'
                      cleared.segSixesOperator = 'include'
                      cleared.segSevensOperator = 'include'
                      cleared.segEightsOperator = 'include'
                      cleared.filterTree = null

                      return cleared
                    })
                  }}
                  onClearReferences={(refs) => {
                    refs.forEach((ref) => {
                      if (ref === 'vendors') {
                        bulkDestroyVendors(dashboardId)
                        setParams((prev) => ({ ...prev, vendors: [], excludedVendors: [] }))
                      } else if (ref === 'employees') {
                        // Clear ALL employee filters
                        bulkDestroyEmployees({ dashboardId })
                        setParams((prev) => ({ ...prev, employees: [], excludedEmployees: [] }))
                      } else if (typeof ref === 'object' && ref.type === 'employees' && ref.database_column_name) {
                        // Clear employees for a specific column only
                        bulkDestroyEmployees({ dashboardId, databaseColumnName: ref.database_column_name })
                        setParams((prev) => ({
                          ...prev,
                          employees: (prev.employees || []).filter(
                            (emp) => emp.database_column_name !== ref.database_column_name
                          ),
                          excludedEmployees: (prev.excludedEmployees || []).filter(
                            (emp) => emp.database_column_name !== ref.database_column_name
                          )
                        }))
                      }
                    })
                  }}
                  onAnalyticSearch={(analyticRecords) => {
                    setParams((prev) => {
                      const currentAnalytics = [...(prev.analytics || [])]
                      analyticRecords.forEach((record) => {
                        const existingIndex = currentAnalytics.findIndex(
                          (a) => a.id === record.analytic_id
                        )
                        const newEntry = {
                          id: record.analytic_id,
                          name: record.name,
                          dashboard_analytic_id: record.id,
                          options: record.options
                        }
                        if (existingIndex >= 0) {
                          currentAnalytics[existingIndex] = newEntry
                        } else {
                          currentAnalytics.push(newEntry)
                        }
                      })
                      return { ...prev, analytics: currentAnalytics }
                    })
                  }}
                  onClearAnalytics={(clearData) => {
                    if (clearData === true) {
                      setParams((prev) => ({ ...prev, analytics: [] }))
                    } else if (Array.isArray(clearData)) {
                      const clearedIds = clearData.map((a) => a.analytic_id)
                      setParams((prev) => ({
                        ...prev,
                        analytics: (prev.analytics || []).filter(
                          (a) => !clearedIds.includes(a.id)
                        )
                      }))
                    }
                  }}
                />
              </Col>
            </Row>
          )}
          <Row className="mt-4">
            <Col>
              <TopTransactions
                params={params}
                setParams={setParams}
                sectionUnit={sectionUnit}
                sectionName={sectionName}
                dashboardId={dashboardId}
                dashboard={dashboard}
                isFilterOpen={isFilterOpen}
                canEdit={canEdit}
                isUserCreator={isUserCreator}
                setDashboardId={setDashboardId}
                referenceSearch={referenceSearch}
                onReferenceSearchHandled={() => setReferenceSearch(null)}
              />
            </Col>
          </Row>
        </>
      )}

      {activeTab !== 'data' && (
      <Card>
        <Card.Body>
          <>
            {activeTab === 'dashboard' && isDashboardError && (
              <CardError
                title="Couldn't load this view"
                onRetry={() => refetchDashboard()}
                isRetrying={isDashboardFetching}
              />
            )}

            {/* react-query v3 leaves a disabled query `idle` rather than `loading`, so gating on
                isLoading keeps the spinner from sticking when the dashboard query is switched
                off (e.g. while the filter drawer is open). */}
            {activeTab === 'dashboard' &&
              !isDashboardError &&
              (isDashboardLoading || isSectionLoading) && (
                <CardLoading label="Loading dashboard…" />
              )}

            {activeTab === 'dashboard' &&
              isSectionSuccess &&
              isDashboardSuccess && (
                <>
                  <Row className="mb-3">
                    <Col className="d-flex align-items-center gap-2">
                      {canEdit && (
                        <Button
                          variant="light"
                          size="sm"
                          data-testid="preview-toggle"
                          onClick={() => setIsPreviewMode(!isPreviewMode)}
                          title={
                            isPreviewMode
                              ? 'Exit Preview Mode'
                              : 'Enter Preview Mode'
                          }
                        >
                          <FontAwesomeIcon
                            icon={isPreviewMode ? faEdit : faEye}
                            className="me-1"
                          />
                          {isPreviewMode ? 'Edit' : 'Preview'}
                        </Button>
                      )}
                      <ExportPdfButton
                        dashboardId={dashboardId}
                        token={token}
                        baseUrl={baseUrl}
                      />
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col>
                      {isDashboardSuccess && (
                        <DashboardGrid
                          dashboardId={dashboardId}
                          sectionId={params.section_id}
                          params={params}
                          canEdit={canEdit}
                          isPreviewMode={isPreviewMode}
                        />
                      )}
                    </Col>
                  </Row>
                </>
              )}

            {activeTab === 'exports' && isDashboardSuccess && (
              <Row>
                <Col>
                  <DashboardExports dashboardId={dashboardId} />
                </Col>
              </Row>
            )}

            {activeTab === 'receipts' && dashboardKey === 'pc_dashboards' && (
              <Row>
                <Col>
                  <ReceiptManagement scanType="receipt" />
                </Col>
              </Row>
            )}

            {activeTab === 'invoice-scans' && dashboardKey === 'ap_dashboards' && (
              <Row>
                <Col>
                  <ReceiptManagement scanType="invoice" />
                </Col>
              </Row>
            )}

            {activeTab === 'samples' && isDashboardSuccess && (
              <Row>
                <Col>
                  <SamplesTab
                    dashboardId={dashboardId}
                    params={params}
                    canEdit={canEdit}
                  />
                </Col>
              </Row>
            )}
          </>
        </Card.Body>
      </Card>
      )}
    </Page>
  )
}

export default Show
